import {
  IBloggerOffers,
  IGetInvoiceRequest,
  IOrderAcceptFinallyRequest,
  ENUM_OFFER_STATUS,
} from "@entities/offer";
import { dateSortingTypes } from "@entities/platform";
import { IOrderFeature } from "@entities/project";
import {
  ADV_ORDERS,
  ADV_PROJECTS,
  authApi,
  BALANCE,
  BLOGGER_OFFERS,
  USER_ME,
  VIEWS_BLOGGER_OFFERS,
} from "@shared/api";
import { INTERSECTION_ELEMENTS } from "@shared/config";
import { ENUM_LANGUAGES_NUM } from "@shared/languages";

export interface getOrdersByStatusReq {
  language: ENUM_LANGUAGES_NUM;
  page: number;
  date_sort: dateSortingTypes;
  elements_on_page?: number;
  status: ENUM_OFFER_STATUS | string;
  search_string?: string;
  order_id?: string;
}

export const bloggerOffersAPI = authApi.injectEndpoints({
  endpoints: (build) => ({
    publishPostBlogger: build.mutation<{ success: boolean }, IOrderFeature>({
      query: (body) => ({
        url: `/order/post/published`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [BLOGGER_OFFERS, ADV_PROJECTS],
    }),
    acceptOffer: build.mutation<{ success: boolean }, IOrderFeature>({
      query: (body) => ({
        url: `/order/blogger/accept`,
        method: "PUT",
        body: body,
      }),
      // invalidatesTags: [
      //   // BLOGGER_OFFERS,
      //   ADV_PROJECTS,
      //   // VIEWS_BLOGGER_OFFERS,
      //   ADV_ORDERS,
      // ],
    }),
    cancelOffer: build.mutation<
      { success: boolean },
      { order_id: string; cancel_reason?: string }
    >({
      query: (body) => ({
        url: `/order/blogger/cancel`,
        method: "PUT",
        body,
      }),
      // invalidatesTags: [
      //   // BLOGGER_OFFERS,
      //   ADV_PROJECTS,
      //   // VIEWS_BLOGGER_OFFERS,
      //   ADV_ORDERS,
      // ],
    }),
    getBloggerOrders: build.query<
      IBloggerOffers,
      getOrdersByStatusReq & { __isWebsocket?: boolean }
    >({
      query: ({ __isWebsocket, ...params }) => ({
        url: `/order/get/blogger`,
        method: `POST`,
        body: params,
      }),
      transformResponse: (response: IBloggerOffers, meta, arg) => {
        const pageSize =
          arg?.elements_on_page ?? INTERSECTION_ELEMENTS.BLOGGER_OFFERS;
        const batchLength = response?.orders?.length ?? 0;

        return {
          ...response,
          status: arg?.status,
          isLast:
            batchLength < pageSize ||
            batchLength + (response?.page - 1) * pageSize >=
              (response?.elements ?? 0),
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { status } = queryArgs;
        return `${endpointName}/${status}/`;
      },
      merge: (currentCache, newItems, arg) => {
        const pageSize =
          arg.arg.elements_on_page ?? INTERSECTION_ELEMENTS.BLOGGER_OFFERS;
        const newMap = new Map(newItems?.orders?.map((p) => [p?.id, p]));

        // Обновляем старые элементы, если есть новые с тем же id
        const updatedOld =
          currentCache?.orders?.map((old) =>
            newMap?.has(old?.id) ? newMap?.get(old?.id)! : old,
          ) || [];

        // Убираем уже обновленные ID из новых, чтобы они не дублировались
        const newIds = new Set(updatedOld?.map((p) => p.id));
        const onlyNew = newItems?.orders?.filter((p) => !newIds.has(p?.id));

        const getIsLast = (orders: IBloggerOffers["orders"]) => {
          const batchLength = newItems?.orders?.length ?? 0;
          const totalElements = newItems?.elements ?? 0;

          return (
            batchLength < pageSize || (orders?.length ?? 0) >= totalElements
          );
        };

        if (arg.arg.__isWebsocket) {
          const orders = [...onlyNew, ...updatedOld];

          return {
            ...currentCache,
            ...newItems,
            orders,
            isLast: getIsLast(orders),
          };
        }

        if (arg.arg.page === 1) {
          return {
            ...newItems,
            isLast: getIsLast(newItems.orders),
          };
        }

        const orders = [...updatedOld, ...onlyNew];

        return {
          ...newItems,
          orders,
          isLast: getIsLast(orders),
        };
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: [BLOGGER_OFFERS],
    }),
    getInvoiceInfo: build.mutation<object, IGetInvoiceRequest>({
      query: ({ order_id, doc_type }) => ({
        url: `/order/invoice-info/${order_id}`,
        method: "GET",
        params: { doc_type },
      }),
    }),

    orderAcceptFinally: build.mutation<
      { success: boolean },
      IOrderAcceptFinallyRequest
    >({
      query: (body) => ({
        url: `/order/blogger/accept-finally`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [BLOGGER_OFFERS, BALANCE, USER_ME],
    }),
  }),
});

export const {
  useAcceptOfferMutation,
  useCancelOfferMutation,
  usePublishPostBloggerMutation,
  useGetBloggerOrdersQuery,
  useGetInvoiceInfoMutation,
  useOrderAcceptFinallyMutation,
} = bloggerOffersAPI;
